import { useCallback, useRef } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';

export const useSetQueryParam = () => {
  const searchParams = useSearchParams();
  const pathname = usePathname();


  // Track the latest intended params synchronously via a ref.
  // useSearchParams() lags behind router.replace(), so rapid-fire calls
  // (e.g., changing finish then sheen) would lose earlier updates without this.
  const paramsRef = useRef(new URLSearchParams(searchParams.toString()));

  // Resync the ref when Next.js commits a new searchParams value or pathname
  // (handles browser back/forward, external navigations, and same-query navigations
  // to a different product where identical searchParams would otherwise leave stale
  // params from the previous product in the ref).
  const prevSearchParams = useRef(searchParams.toString());
  const prevPathname = useRef(pathname);
  if (searchParams.toString() !== prevSearchParams.current || pathname !== prevPathname.current) {
    prevSearchParams.current = searchParams.toString();
    prevPathname.current = pathname;
    paramsRef.current = new URLSearchParams(searchParams.toString());
  }

  const setQueryParam = useCallback(
    (name: string, value: string) => {
      paramsRef.current.set(name, value);
      const qs = paramsRef.current.toString();
      const currentPathname = typeof window !== 'undefined' ? window.location.pathname : pathname;
      const newPath = qs ? currentPathname + '?' + qs : currentPathname;
      if (typeof window !== 'undefined') {
        window.history.replaceState({ ...window.history.state, as: newPath, url: newPath }, '', newPath);
      }
    },
    [pathname],
  );

  const removeQueryParam = useCallback(
    (name: string) => {
      paramsRef.current.delete(name);
      const qs = paramsRef.current.toString();
      const currentPathname = typeof window !== 'undefined' ? window.location.pathname : pathname;
      const newPath = qs ? currentPathname + '?' + qs : currentPathname;
      if (typeof window !== 'undefined') {
        window.history.replaceState({ ...window.history.state, as: newPath, url: newPath }, '', newPath);
      }
    },
    [pathname],
  );

  return { setQueryParam, removeQueryParam };
};
